import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { HubShell, Section, InfoGrid, hubHead } from "@/components/work-hubs/HubShell";

export const Route = createFileRoute("/app-testing/")({
  head: () =>
    hubHead(
      "/app-testing",
      "Get Paid to Test Apps — Earnings Calculator & Honest Guide | DrHint",
      "Estimate realistic app and website testing income, understand the types of testing platforms, and learn what to expect before you sign up.",
    ),
  component: Page,
});

function Page() {
  const [tests, setTests] = useState(3);
  const [rate, setRate] = useState(10);
  const [accept, setAccept] = useState(50);
  const weekly = tests * rate * (accept / 100);
  const monthly = weekly * 4.3;

  return (
    <HubShell
      crumbs={[{ label: "Home", to: "/" }, { label: "App Testing" }]}
      step={1}
      eyebrow="Testing Hub"
      title="App & Website Testing: What Can You Really Earn?"
      intro="User-feedback testing means trying an app or website and recording your honest thoughts. It is real paid work, but tests are limited and you must qualify for each one. Use the calculator to set realistic expectations."
      next={{ to: "/app-testing/guidelines", label: "View Tester Benchmark Guidelines" }}
    >
      <Section title="Earnings potential calculator">
        <div className="rounded-2xl border border-border bg-card p-5 space-y-5">
          <label className="block">
            <span className="text-sm font-semibold text-foreground">Tests available to you per week: {tests}</span>
            <input type="range" min={1} max={20} value={tests} onChange={(e) => setTests(Number(e.target.value))} className="mt-2 w-full" />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-foreground">Average pay per test: ${rate}</span>
            <input type="range" min={3} max={60} value={rate} onChange={(e) => setRate(Number(e.target.value))} className="mt-2 w-full" />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-foreground">Screeners you pass: {accept}%</span>
            <input type="range" min={10} max={100} step={5} value={accept} onChange={(e) => setAccept(Number(e.target.value))} className="mt-2 w-full" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-muted/50 p-4"><div className="text-xs text-muted-foreground">Per week</div><div className="text-2xl font-black text-foreground">${weekly.toFixed(0)}</div></div>
            <div className="rounded-xl bg-muted/50 p-4"><div className="text-xs text-muted-foreground">Per month (est.)</div><div className="text-2xl font-black text-foreground">${monthly.toFixed(0)}</div></div>
          </div>
          <p className="text-xs text-muted-foreground">Most new testers outside the US and UK see only a few tests per week. Treat this as side income, not a salary.</p>
        </div>
      </Section>

      <Section title="Types of testing platforms">
        <InfoGrid items={[
          { title: "Recorded usability tests", body: "Complete tasks on a website or app while speaking your thoughts aloud. Usually 10–20 minutes each." },
          { title: "Game playtesting", body: "Play mobile games before release and give feedback. Often recorded on your phone or tablet." },
          { title: "Live interviews", body: "Scheduled video calls with researchers. Higher pay, but fewer slots and stricter screening." },
          { title: "Functional (QA) testing", body: "Find and report bugs following test cases. More technical, sometimes paid per valid bug or per hour." },
        ]} />
      </Section>

      <Section title="Honest expectations">
        <p>Testing sites are free to join. Any "app testing" offer that asks you to deposit money, buy a plan or recruit friends is not real testing work. Payment usually arrives via PayPal or a similar service 1–2 weeks after a test is approved.</p>
      </Section>
    </HubShell>
  );
}
